import React from "react";
import { ClipboardIcon, FileCheck, HistoryIcon } from "lucide-react";
import { toDayMonthNameFormat, formatTimeToAmPm } from "@/helpers/dates";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { BusinessReport } from "@/modules/snapshots/businessReports.types";
import { CreateBusinessReportModal } from "../modals/createModal";
import { BusinessReportSelectFileModal } from "../modals/selectFileModal";
import { BusinessReportPromptListModal } from "../modals/promptListModal/businessReportPromptListModal";
import { BusinessReportTemplateCardActions } from "..//components/businessReportTemplateCardActions";

interface BusinessReportTemplateCardProps {
  businessReport: BusinessReport;
}

export const BusinessReportTemplateCard: React.FC<BusinessReportTemplateCardProps> = ({ businessReport }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isPromptsModalOpen, setIsPromptsModalOpen] = React.useState(false);
  const userData = useAuthenticatedUser();
  const [isEditOpen, setIsEditOpen] = React.useState(false);

  const onClose = () => {
    setIsOpen(false);
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const handleTopicsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setIsPromptsModalOpen(true);
  };

  return (
    <>
      {isEditOpen && (
        <CreateBusinessReportModal businessReport={businessReport} isOpen={isEditOpen} setIsOpen={setIsEditOpen} />
      )}
      {isOpen && <BusinessReportSelectFileModal businessReport={businessReport} onClose={onClose} />}
      {isPromptsModalOpen && (
        <BusinessReportPromptListModal businessReport={businessReport} onClose={() => setIsPromptsModalOpen(false)} />
      )}

      <div
        className="group relative flex h-full w-full max-w-[450px] cursor-pointer flex-col rounded-xl border border-gray-200 bg-ui-card-background p-4 transition-all hover:border-desert-sand-300 hover:shadow-xl sm:p-5 md:p-6"
        onClick={handleOnClick}
      >
        <div className="mb-4 flex items-start gap-3 sm:mb-5 md:mb-6">
          <div className="rounded-lg bg-desert-sand-200 p-2 sm:p-2.5 md:p-3">
            <ClipboardIcon className="h-5 w-5 flex-shrink-0 text-midnight-blue-600 sm:h-6 sm:w-6" />
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            <h3 className="break-words text-base font-semibold leading-tight text-gray-900 transition-colors group-hover:text-midnight-blue-600 sm:text-lg md:text-xl">
              {businessReport.name}
            </h3>
            {businessReport.variables.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="bg-desert-sand-100/50 inline-flex items-center whitespace-nowrap rounded-md px-2 py-1 text-xs text-desert-sand-800 sm:text-sm">
                  Variables required
                </span>
                <FileCheck className="h-3.5 w-3.5 text-desert-sand-400 sm:h-4 sm:w-4" />
              </div>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col justify-between gap-6">
          <p className="break-words text-sm leading-relaxed text-gray-700 sm:text-base md:line-clamp-3">
            {businessReport.description}
          </p>

          <div className="mt-auto flex flex-col gap-4 border-t border-gray-100 pt-4 sm:pt-5">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-desert-sand-400">Template contains:</span>
                <div
                  onClick={handleTopicsClick}
                  className="inline-flex cursor-pointer items-center justify-center rounded-full bg-desert-sand-100 px-3.5 py-1.5 text-xs font-medium text-midnight-blue-700 hover:bg-desert-sand-200 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {businessReport.prompts.length} Topics
                </div>
              </div>
              {businessReport.last_used_at && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-desert-sand-400">Last used:</span>
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      <HistoryIcon className="relative top-[2px] h-5 w-5 text-desert-sand-400 sm:h-6 sm:w-6" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <span className="text-xs font-medium text-gray-700 sm:text-sm">
                        {toDayMonthNameFormat(businessReport.last_used_at)}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-desert-sand-500 sm:text-sm">
                        <span>{formatTimeToAmPm(businessReport.last_used_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {userData.features.includes("business_report_admin") && (
                <BusinessReportTemplateCardActions businessReport={businessReport} setIsEditOpen={setIsEditOpen} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
